import { FileAttributes } from "@pulse/pulse-rpcs/lib/types/fileAttributes";
import { Instance, flow, getRoot, types } from "mobx-state-tree";
import {
  FileUploadError,
  getFileSHA,
  uploadFileToS3,
  validateRespondentSPoCCSVFile,
} from "../../utils/FileUploadUtils";
import {
  useGetPresignedDocumentUploadURLForRespondentSpocMappingRPCClientImpl,
  useGetPresignedDownloadURLForRespondentSpocMappingTemplateRPCClientImpl,
  useValidateCSVAndBulkAssignSpocsToRespondentsRPCClientImpl,
} from "../rpcs/RPC";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import {
  CSV_FILE_TYPE,
  MAX_FILE_NAME,
  MAX_RESPONDENT_SPOC_MAPPING_FILE_SIZE_IN_BYTES,
  MIN_FILE_NAME,
  MIN_FILE_SIZE_IN_BYTES,
  NetworkingError,
  RESPONDENT_SPOC_MAPPING_CSV_TEMPLATE_NAME,
  SHA_256_LENGTH,
} from "@pulse/shared-components";
import {
  downloadFile,
  insertDocumentRecord,
} from "../../utils/FileDownloadUtils";
import { getAPIClient } from "../../networking/APIClient";
import { RootStore } from "../../root/store/RootStore";
import { GetPresignedDownloadURLForRespondentSpocMappingTemplateRPC } from "@pulse/pulse-rpcs/lib/spoc-management/getPresignedDownloadUrlForRespondentSpocMappingTemplateRPC";
import { ValidateCSVAndBulkAssignSpocsToRespondentsRPC } from "@pulse/pulse-rpcs/lib/spoc-management/validateCsvAndBulkAssignSpocsToRespondentsRPC";
import { GetPresignedDocumentUploadURLForRespondentSpocMappingRPC } from "@pulse/pulse-rpcs/lib/spoc-management/getPresignedDocumentUploadUrlForRespondentSpocMappingRPC";

export enum ValidateRespondentSPoCMappingErrors {
  InvalidFile = "INVALID_FILE",
  UnknownFile = "UNKNOWN_FILE",
  SpocAssignmentInProgress = "SPOC_ASSIGNMENT_IN_PROGRESS",
  ProjectArchived = "PROJECT_ARCHIVED",
}

export enum GetPresignedDocumentUploadURLForRespondentSpocMappingErrors {
  SpocAssignmentInProgress = "SPOC_ASSIGNMENT_IN_PROGRESS",
  InvalidProjectId = "INVALID_PROJECT_ID",
  ProjectArchived = "PROJECT_ARCHIVED",
}

export enum RespondentSPoCMappingDialogState {
  initialState = "INITIAL_STATE",
  fileUploaded = "FILE_UPLOADED",
  fileValidated = "FILE_VALIDATED",
  templateFileDownloading = "TEMPLATE_FILE_DOWNLOADING",
}

export const RespondentSPoCMappingStore = types
  .model("RespondentSPoCMappingStore", {
    fileName: types.maybe(types.string),
    documentId: types.maybe(types.string),
    isRPCLoading: types.optional(types.boolean, false),
    validationErrors: types.maybeNull(
      types.enumeration(Object.values(FileUploadError)),
    ),
    rpcErrors: types.maybeNull(
      types.union(
        types.enumeration(
          Object.values(
            GetPresignedDocumentUploadURLForRespondentSpocMappingErrors,
          ),
        ),
        types.enumeration(Object.values(ValidateRespondentSPoCMappingErrors)),
        types.enumeration(Object.values(NetworkingError)),
      ),
    ),
    invalidFileDetails: types.maybeNull(types.string),
    respondentSPoCMappingDialogState: types.optional(
      types.enumeration(
        "RespondentSPoCMappingDialogState",
        Object.values(RespondentSPoCMappingDialogState),
      ),
      RespondentSPoCMappingDialogState.initialState,
    ),
    isRespondentSPoCMappingUploadDialogVisible: types.optional(
      types.boolean,
      false,
    ),
    isRespondentSPoCAssignmentValidationDialogVisible: types.optional(
      types.boolean,
      false,
    ),
  })
  .views((store) => ({
    get isFileValidated(): boolean {
      return (
        store.respondentSPoCMappingDialogState ===
        RespondentSPoCMappingDialogState.fileValidated
      );
    },
    get hasFileUploaded(): boolean {
      return (
        store.respondentSPoCMappingDialogState ===
        RespondentSPoCMappingDialogState.fileUploaded
      );
    },
    get doesStoreContainErrors(): boolean {
      return store.rpcErrors !== null;
    },
    get isFileInvalid(): boolean {
      return (
        store.rpcErrors === ValidateRespondentSPoCMappingErrors.InvalidFile
      );
    },
  }))
  .actions((store) => ({
    setIsRespondentSPoCMappingUploadDialogVisible: (
      isDialogVisible: boolean,
    ): void => {
      store.isRespondentSPoCMappingUploadDialogVisible = isDialogVisible;
    },
    setIsRespondentSPoCAssignmentValidationDialogVisible: (
      isDialogVisible: boolean,
    ): void => {
      store.isRespondentSPoCAssignmentValidationDialogVisible = isDialogVisible;
    },
    resetRPCErrors: (): void => {
      store.rpcErrors = null;
    },
    resetValidationErrors: (): void => {
      store.validationErrors = null;
    },
    resetSPoCMappingCSVStates: (): void => {
      store.respondentSPoCMappingDialogState =
        RespondentSPoCMappingDialogState.initialState;
    },
    setFileName: (fileName: string | undefined): void => {
      store.fileName = fileName;
    },
    getFileAttribute: flow(function* (file: File) {
      if (!CSV_FILE_TYPE.includes(file.type)) {
        store.validationErrors = FileUploadError.InvalidFileFormat;
        return;
      }
      const sha256 = yield getFileSHA(file);

      try {
        yield validateRespondentSPoCCSVFile(file)
          .then((csv) => csv)
          .catch((error) => {
            throw new Error(error);
          });
      } catch (error) {
        if (error instanceof Error) {
          switch (error.message) {
            case FileUploadError.BaseColumnsNotFound: {
              store.validationErrors = FileUploadError.BaseColumnsNotFound;
              return;
            }
            case FileUploadError.MaxColumnsExceeded: {
              store.validationErrors = FileUploadError.MaxColumnsExceeded;
              return;
            }
            case FileUploadError.MaxRowsExceeded: {
              store.validationErrors = FileUploadError.MaxRowsExceeded;
              return;
            }
          }
        }
      }

      try {
        if (sha256.length !== SHA_256_LENGTH) {
          store.validationErrors = FileUploadError.InvalidFileSHA;
          return;
        } else if (
          file.name.length < MIN_FILE_NAME ||
          file.name.length > MAX_FILE_NAME
        ) {
          store.validationErrors = FileUploadError.InvalidFileName;
          return;
        } else if (file.size > MAX_RESPONDENT_SPOC_MAPPING_FILE_SIZE_IN_BYTES) {
          store.validationErrors = FileUploadError.MaxFileSizeReached;
          return;
        } else if (file.size < MIN_FILE_SIZE_IN_BYTES) {
          store.validationErrors = FileUploadError.MinFileSizeNotReached;
          return;
        }

        const fileAttribute = new FileAttributes(sha256, file.name, file.size);
        return fileAttribute;
      } catch (error) {
        store.validationErrors = FileUploadError.InternalError;
      }
    }),
  }))
  .actions((store) => ({
    uploadCSVFile: flow(function* (file: File, projectId: string) {
      store.isRPCLoading = true;
      store.resetValidationErrors();
      store.resetSPoCMappingCSVStates();
      store.resetRPCErrors();
      try {
        const apiClient = getAPIClient(store);

        const fileAttribute: FileAttributes | undefined =
          yield store.getFileAttribute(file);

        if (fileAttribute === undefined) {
          return;
        }

        const {
          response,
          error,
        }: {
          response?: GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.Response;
          error?: GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.Errors.Errors;
        } =
          yield useGetPresignedDocumentUploadURLForRespondentSpocMappingRPCClientImpl(
            apiClient,
          ).execute(
            new GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.Request(
              fileAttribute,
              new LeoUUID(projectId),
            ),
          );

        if (response) {
          try {
            yield uploadFileToS3(response.documentURL, file);
            store.documentId = yield insertDocumentRecord(
              fileAttribute,
              apiClient,
            );
            store.respondentSPoCMappingDialogState =
              RespondentSPoCMappingDialogState.fileUploaded;
          } catch (e) {
            store.respondentSPoCMappingDialogState =
              RespondentSPoCMappingDialogState.initialState;
            store.validationErrors = FileUploadError.InternalError;
          }
        } else if (error) {
          switch (error.code) {
            case GetPresignedDocumentUploadURLForRespondentSpocMappingRPC
              .RPCError.SpocAssignmentInProgress: {
              store.rpcErrors =
                GetPresignedDocumentUploadURLForRespondentSpocMappingErrors.SpocAssignmentInProgress;
              break;
            }
            case GetPresignedDocumentUploadURLForRespondentSpocMappingRPC
              .RPCError.InvalidProjectId: {
              store.rpcErrors =
                GetPresignedDocumentUploadURLForRespondentSpocMappingErrors.InvalidProjectId;
              break;
            }
            case GetPresignedDocumentUploadURLForRespondentSpocMappingRPC
              .RPCError.ProjectAlreadyArchived: {
              store.rpcErrors =
                GetPresignedDocumentUploadURLForRespondentSpocMappingErrors.ProjectArchived;
              break;
            }
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          const rootStore = getRoot<typeof RootStore>(store);
          rootStore.networkingStore.errorStore.setLeoError(e);
        } else {
          console.error(`Unhandled error occured: ${e}`);
        }
      } finally {
        store.isRPCLoading = false;
      }
    }),
    validateAndUpdateRespondentSPoCMapping: flow(function* (projectId: string) {
      store.validationErrors = null;
      store.rpcErrors = null;
      store.isRPCLoading = true;
      store.resetSPoCMappingCSVStates();

      try {
        const apiClient = getAPIClient(store);

        const request =
          new ValidateCSVAndBulkAssignSpocsToRespondentsRPC.Request(
            new LeoUUID(projectId),
            new LeoUUID(store.documentId),
          );
        const {
          error,
        }: {
          error?: ValidateCSVAndBulkAssignSpocsToRespondentsRPC.Errors.Errors;
        } =
          yield useValidateCSVAndBulkAssignSpocsToRespondentsRPCClientImpl(
            apiClient,
          ).execute(request);

        if (error) {
          switch (error.code) {
            case ValidateCSVAndBulkAssignSpocsToRespondentsRPC.RPCError
              .InvalidProjectId:
              break;
            case ValidateCSVAndBulkAssignSpocsToRespondentsRPC.RPCError
              .InvalidFile: {
              if (
                error instanceof
                ValidateCSVAndBulkAssignSpocsToRespondentsRPC.Errors.InvalidFile
              ) {
                store.invalidFileDetails = error.invalidFileDetails;
              }
              store.rpcErrors = ValidateRespondentSPoCMappingErrors.InvalidFile;
              break;
            }
            case ValidateCSVAndBulkAssignSpocsToRespondentsRPC.RPCError
              .ProjectArchived: {
              store.rpcErrors =
                ValidateRespondentSPoCMappingErrors.ProjectArchived;
              break;
            }
            case ValidateCSVAndBulkAssignSpocsToRespondentsRPC.RPCError
              .SpocAssignmentInProgress: {
              store.rpcErrors =
                ValidateRespondentSPoCMappingErrors.SpocAssignmentInProgress;
              break;
            }
            case ValidateCSVAndBulkAssignSpocsToRespondentsRPC.RPCError
              .UnknownFile: {
              store.rpcErrors = ValidateRespondentSPoCMappingErrors.UnknownFile;
              break;
            }
          }
        }
      } catch (e) {
        // If there is an error, we need to set the respondent SPoC mapping dialog to not be visible, since it will go to the success state once the RPC is called.
        // store.setIsRespondentSPoCMappingDialogVisible(false);
        if (e instanceof Error) {
          const rootStore = getRoot<typeof RootStore>(store);
          rootStore.networkingStore.errorStore.setLeoError(e);
        } else {
          console.error(`Unhandled error occured: ${e}`);
        }
      } finally {
        store.isRPCLoading = false;
        store.respondentSPoCMappingDialogState =
          RespondentSPoCMappingDialogState.fileValidated;
      }
    }),
    downloadRespondentSpocMappingTemplate: flow(function* () {
      store.respondentSPoCMappingDialogState =
        RespondentSPoCMappingDialogState.templateFileDownloading;
      try {
        const apiClient = getAPIClient(store);
        const request =
          new GetPresignedDownloadURLForRespondentSpocMappingTemplateRPC.Request();
        const {
          response,
          error,
        }: {
          response?: GetPresignedDownloadURLForRespondentSpocMappingTemplateRPC.Response;
          error?: GetPresignedDownloadURLForRespondentSpocMappingTemplateRPC.Errors.Errors;
        } =
          yield useGetPresignedDownloadURLForRespondentSpocMappingTemplateRPCClientImpl(
            apiClient,
          ).execute(request);

        if (response) {
          yield downloadFile(
            response.documentURL.href,
            RESPONDENT_SPOC_MAPPING_CSV_TEMPLATE_NAME,
          );
        }
        if (error) {
          store.rpcErrors = NetworkingError.InternalError;
        }
      } catch (e) {
        if (e instanceof Error) {
          const rootStore = getRoot<typeof RootStore>(store);
          rootStore.networkingStore.errorStore.setLeoError(e);
        } else {
          console.error(`Unhandled error occured: ${e}`);
        }
      } finally {
        store.resetSPoCMappingCSVStates();
      }
    }),
  }));

export const createRespondentSPoCMappingStore = (): Instance<
  typeof RespondentSPoCMappingStore
> => {
  return RespondentSPoCMappingStore.create();
};
